const KEY = "afrimed_cookies_v2";
const LEGACY_KEY = "afrimed_cookies_v1";
const COOKIE_KEY = "afrimed_cookie_consent_v2";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
export const CONSENT_EVENT = "afrimed:consent-changed";

export const CONSENT_CATEGORIES = [
  "strictly",
  "statistics",
  "performance",
  "functionality",
  "targeting",
  "social",
  "beacons",
];

export const DEFAULT_CONSENT = Object.freeze({
  strictly: true,
  statistics: false,
  performance: false,
  functionality: false,
  targeting: false,
  social: false,
  beacons: false,
});

function normaliseChoices(input) {
  const src = input && typeof input === "object" ? input : {};
  const out = { ...DEFAULT_CONSENT };
  for (const key of CONSENT_CATEGORIES) {
    if (key === "strictly") {
      out.strictly = true;
      continue;
    }
    out[key] = !!src[key];
  }
  return out;
}

function buildState(raw = {}) {
  const choices = normaliseChoices(raw?.choices || raw);
  return {
    version: 2,
    decided: !!raw?.decided,
    dismissed: !!raw?.dismissed,
    decidedAt: String(raw?.decidedAt || ""),
    choices,
  };
}

function tryParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readCookieValue(name) {
  if (typeof document === "undefined") return "";
  const escaped = String(name || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  if (!match) return "";
  try {
    return decodeURIComponent(match[1] || "");
  } catch {
    return String(match[1] || "");
  }
}

function writeConsentCookie(state) {
  if (typeof document === "undefined") return;
  const encoded = encodeURIComponent(JSON.stringify(state));
  const parts = [
    `${COOKIE_KEY}=${encoded}`,
    `Max-Age=${COOKIE_MAX_AGE}`,
    "Path=/",
    "SameSite=Lax",
  ];
  if (window?.location?.protocol === "https:") {
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

function clearConsentCookie() {
  if (typeof document === "undefined") return;
  const parts = [`${COOKIE_KEY}=`, "Max-Age=0", "Path=/", "SameSite=Lax"];
  if (window?.location?.protocol === "https:") {
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

export function readConsentState() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const state = buildState(tryParse(raw) || {});
      writeConsentCookie(state);
      return state;
    }

    const cookieRaw = readCookieValue(COOKIE_KEY);
    if (cookieRaw) {
      const hydrated = buildState(tryParse(cookieRaw) || {});
      try {
        localStorage.setItem(KEY, JSON.stringify(hydrated));
        localStorage.setItem(LEGACY_KEY, JSON.stringify(hydrated.choices));
      } catch {
        // ignore storage errors
      }
      return hydrated;
    }

    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (legacyRaw) {
      const migrated = buildState({
        decided: true,
        dismissed: false,
        decidedAt: "",
        choices: tryParse(legacyRaw) || {},
      });
      writeConsentState(migrated);
      return migrated;
    }
  } catch {
    // ignore storage errors
  }

  return buildState();
}

export function writeConsentState(next) {
  const state = buildState(next);
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    // Keep legacy key in sync for older app code paths.
    localStorage.setItem(LEGACY_KEY, JSON.stringify(state.choices));
  } catch {
    // ignore storage errors
  }
  writeConsentCookie(state);
  return state;
}

export function clearConsentState() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // ignore storage errors
  }
  clearConsentCookie();
}

export function emitConsentChanged(state) {
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: { state } })
    );
  } catch {
    // ignore event errors
  }
}

export function onConsentChanged(listener) {
  if (typeof listener !== "function") return () => {};
  const handler = (event) => listener(event?.detail?.state || readConsentState());
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}

export const getConsent = () => readConsentState().choices;

export const hasConsent = (key) => {
  if (key === "strictly") return true;
  const state = readConsentState();
  if (!state.decided) return false;
  return !!state.choices?.[key];
};

/** Wrap an onClick so it only runs when consent is present; otherwise opens the manager */
export const withConsent = (need, fn) => (...args) => {
  if (hasConsent(need)) return fn?.(...args);
  window.afrimedCookies?.open?.();
};
